import React, { useEffect, useMemo, useState } from 'react';

import { useLazyQuery, useMutation } from '@apollo/client';
import { UPDATE_CONTACTS_MUTATION } from 'Queries/Mutations';
import { PUBLIC_GET_CIAM_STATUS_BY_EMAIL } from 'Queries/Queries';
import energyIcon from 'assets/pics/energy_icon.svg';
import gasIcon from 'assets/pics/gas_icon.svg';
import { useFormik } from 'formik';
import _, { uniqueId } from 'lodash';
import PropTypes from 'prop-types';

import { ActionButton } from 'components/ActionButton/ActionButton';
import { PUBLIC_NO_AUTH_CONFIG } from 'components/ApolloComponent/Apollo';
import {
  getCiamEmailErrorContent,
  getCiamEmailFieldErrorMessage,
} from 'components/CiamEmail/CiamEmailErrors';
import { CustomModal } from 'components/CustomModal';
import {
  doesExpertHaveMainBranch,
  getDEExpertPopupErrorMessage,
} from 'components/DEBForm/Hooks/utils';
import { YExpertPopup } from 'components/DEBForm/ValidationStructure/YExpert';
import { DEBExpertPopupContactSection } from 'components/DebFormComponents/Popup/DEBExpertPopupContactSection';
import { DEBExpertPopupValiditySection } from 'components/DebFormComponents/Popup/DEBExpertPopupValiditySection';
import {
  EXPERT_CONTACT_ERRORS,
  buildExpertContactValidationFields,
  getExpertContactFormErrorMessage,
  getExpertEmails,
  hasExpertContactValues,
  resolveExpertContactEmail,
  setCreateExpertEmailFieldErrors,
  submitExpertContactsUpdate,
} from 'components/DebFormComponents/Popup/expertContact.utils';
import { ErrorBanner } from 'components/ErrorBanner/ErrorBanner';
import { FormActionButtons } from 'components/FormHelper/FormActionButtons';
import { LabelCheckbox } from 'components/LabelCheckbox/LabelCheckbox';

import { BRANCH_TYPE, EIV_STATUS } from 'global/constants';
import { evaluateCondition } from 'global/fieldValidationFunction';
import { FIRST_QUALIFICATIONS, SECOND_QUALIFICATIONS } from 'global/qualifications';

import { FFormDebug } from 'utils/FFormDebug';
import { areExpertContactsEnabled } from 'utils/FormUtils';
import {
  doesRegisteredInstallerHaveBranch,
  isRegisteredBranchGuest,
  isRegisteredBranchMain,
} from 'utils/InstallerUtils';
import { checkCiamEmailByQuery } from 'utils/ciamEmailValidation.utils';

import { DEBExpertPopupBaseInfoSection } from './DEBExpertPopupBaseInfoSection';
import { DEBExpertPopupMainBranchSection } from './DEBExpertPopupMainBranchSection';

import './Popup.sass';

const calculateInitialValues = (installerStatusSummary, forceEnergy, forceGas) => {
  // forced to set the id to 00000 as using randomUUID here will
  // cause the formik to rerender indefintely
  return {
    id: '0000000',
    lastName: '',
    firstName: '',
    isExpertEnergyActive: forceEnergy,
    isExpertGasActive: forceGas,
    birthdate: '',
    expertEnergyValidityDate: '',
    expertGasValidityDate: '',
    isExpertOwner: undefined,
    firstQualificationEnergy: '',
    secondQualificationEnergy: '',
    firstQualificationGas: '',
    secondQualificationGas: '',
    isMainEnergy: isRegisteredBranchMain(installerStatusSummary, BRANCH_TYPE.ENERGY),
    isMainGas: isRegisteredBranchMain(installerStatusSummary, BRANCH_TYPE.GAS),
    expertexpertGasValidityDate: '',
    expertexpertEnergyValidityDate: '',
    statusEnergy: forceEnergy ? EIV_STATUS.NEW : EIV_STATUS.NOT_SET,
    statusGas: forceGas ? EIV_STATUS.NEW : EIV_STATUS.NOT_SET,
    deletionDateEnergy: '',
    deletionDateGas: '',
    deletionReasonGas: '',
    deletionReasonEnergy: '',
    netzportalEmail: '',
    confirmEmail: '',
    newEmail: '',
    telefon: '',
    isNewExpert: true,
  };
};

export const DEBCreateExpertPopup = ({
  mainFormik,
  selectedRow,
  setSelectedRow,
  popupOpen,
  setPopupOpen,
  isGuest,
  gasEntryType,
  energyEntryType,
  installerStatusSummary,
  onExpertContactSaved,
}) => {
  const [apiError, setApiError] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [pendingExpert, setPendingExpert] = useState(null);
  const [ciamEmailError, setCiamEmailError] = useState(null);
  const [isCheckingCiamEmail, setIsCheckingCiamEmail] = useState(false);
  const doesEnergyExist = useMemo(
    () => doesRegisteredInstallerHaveBranch(BRANCH_TYPE.ENERGY, installerStatusSummary),
    [installerStatusSummary]
  );
  const energyGuest = useMemo(
    () => isRegisteredBranchGuest(installerStatusSummary, BRANCH_TYPE.ENERGY),
    [installerStatusSummary]
  );
  const doesGasExist = useMemo(
    () => doesRegisteredInstallerHaveBranch(BRANCH_TYPE.GAS, installerStatusSummary),
    [installerStatusSummary]
  );
  const gasGuest = useMemo(
    () => isRegisteredBranchGuest(installerStatusSummary, BRANCH_TYPE.GAS),
    [installerStatusSummary]
  );

  const forceEnergy = useMemo(
    () => doesEnergyExist && !doesGasExist,
    [doesEnergyExist, doesGasExist]
  );
  const forceGas = useMemo(() => !doesEnergyExist && doesGasExist, [doesEnergyExist, doesGasExist]);

  const initialValues = useMemo(
    () => calculateInitialValues(installerStatusSummary, forceEnergy, forceGas),
    [installerStatusSummary, forceEnergy, forceGas]
  );

  const expertsEmails = useMemo(
    () => getExpertEmails(mainFormik.values.experts),
    [mainFormik.values.experts]
  );

  const validationSchema = useMemo(
    () =>
      YExpertPopup.shape(
        areExpertContactsEnabled
          ? buildExpertContactValidationFields({
              forbiddenMails: expertsEmails,
              isEditing: false,
            })
          : {}
      ),
    [expertsEmails]
  );

  const formik = useFormik({
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues,
  });
  const originalExpertData = mainFormik?.values?.experts?.[selectedRow];

  const [updateContactsMutation, { loading: isSavingContacts }] =
    useMutation(UPDATE_CONTACTS_MUTATION);

  const [getCiamStatusByEmail] = useLazyQuery(
    PUBLIC_GET_CIAM_STATUS_BY_EMAIL,
    PUBLIC_NO_AUTH_CONFIG
  );

  const firstQualificationEnergyItems = useMemo(
    () =>
      Object.keys(FIRST_QUALIFICATIONS)
        .filter((qualificationsKey) =>
          evaluateCondition(FIRST_QUALIFICATIONS[qualificationsKey].whenShouldFieldRender, {
            branch: BRANCH_TYPE.ENERGY,
          })
        )
        .map((qualificationsKey) => FIRST_QUALIFICATIONS[qualificationsKey]),
    []
  );

  const firstQualificationGasItems = useMemo(
    () =>
      Object.keys(FIRST_QUALIFICATIONS)
        .filter((qualificationsKey) =>
          evaluateCondition(FIRST_QUALIFICATIONS[qualificationsKey].whenShouldFieldRender, {
            branch: BRANCH_TYPE.GAS,
          })
        )
        .map((qualificationsKey) => FIRST_QUALIFICATIONS[qualificationsKey]),
    []
  );

  const onCancel = () => {
    if (updateSuccess && pendingExpert) {
      mainFormik.setFieldValue(`experts`, [...mainFormik.values.experts, pendingExpert]);
      onExpertContactSaved?.({
        id: pendingExpert.id,
        telefon: pendingExpert.telefon,
        netzportalEmail: pendingExpert.netzportalEmail,
      });
    }
    formik.resetForm();
    setSelectedRow(null);
    setPopupOpen(false);
    setApiError(false);
    setUpdateSuccess(false);
    setPendingExpert(null);
    setCiamEmailError(null);
  };

  const addExpertToForm = (expert) => {
    mainFormik.setFieldValue(`experts`, [...mainFormik.values.experts, expert]);
    formik.resetForm();
    setSelectedRow(null);
    setPopupOpen(false);
    setPendingExpert(null);
  };

  const onSubmit = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      return;
    }

    const email = formik.values.netzportalEmail?.trim();
    if (email) {
      setIsCheckingCiamEmail(true);
      setCiamEmailError(null);
      const ciamResult = await checkCiamEmailByQuery(getCiamStatusByEmail, email);
      setIsCheckingCiamEmail(false);

      if (!ciamResult.isValid) {
        const fieldError = getCiamEmailFieldErrorMessage(ciamResult.errorType);
        setCreateExpertEmailFieldErrors(formik, fieldError);
        setCiamEmailError(getCiamEmailErrorContent(ciamResult.errorType));
        return;
      }
    }

    const newExpert = {
      ...formik.values,
      completeName: `${formik.values.firstName} ${formik.values.lastName}`,
      id: Number(uniqueId()),
      isNewExpert: true,
    };

    if (!areExpertContactsEnabled || !hasExpertContactValues(formik.values, { isEditing: false })) {
      addExpertToForm(newExpert);
      return;
    }

    submitExpertContactsUpdate(updateContactsMutation, newExpert.id, formik.values, {
      isEditing: false,
    })
      .then((result) => {
        if (result.duplicateEmail) {
          setCreateExpertEmailFieldErrors(formik, EXPERT_CONTACT_ERRORS.DUPLICATE_EMAIL);
          return;
        }

        if (result.needsEmailConfirmation) {
          setPendingExpert({
            ...newExpert,
            netzportalEmail: resolveExpertContactEmail(formik.values),
            telefon: formik.values.telefon,
          });
          setUpdateSuccess(true);
          return;
        }

        addExpertToForm({
          ...newExpert,
          netzportalEmail: result.netzportalEmail,
          telefon: result.telefon,
        });
        onExpertContactSaved?.({
          id: newExpert.id,
          telefon: result.telefon,
          netzportalEmail: result.netzportalEmail,
        });
      })
      .catch(() => {
        addExpertToForm(newExpert);
      });
  };

  const secondQualficationGasItems = useMemo(() => {
    const secondQualficationItems = Object.keys(SECOND_QUALIFICATIONS)
      .filter((qualificationsKey) =>
        evaluateCondition(SECOND_QUALIFICATIONS[qualificationsKey].whenShouldFieldRender, {
          branch: BRANCH_TYPE.GAS,
          firstQualificationGas: formik.values.firstQualificationGas,
        })
      )
      .map((qualificationsKey) => SECOND_QUALIFICATIONS[qualificationsKey]);

    return secondQualficationItems;
  }, [formik.values.firstQualificationGas]);

  useEffect(() => {
    setCiamEmailError(null);
  }, [formik.values.netzportalEmail]);

  useEffect(() => {
    if (!popupOpen) {
      return;
    }
    // used to automatically set the second qualification gas value if there is only one item
    const checkAndSetSingleSecondQualificationGas = () => {
      if (secondQualficationGasItems.length === 1) {
        formik.setFieldValue(`secondQualificationGas`, secondQualficationGasItems[0].value);
      }
    };
    checkAndSetSingleSecondQualificationGas();
  }, [secondQualficationGasItems, popupOpen, formik.values.secondQualificationGas]);

  const secondQualficationEnergyItems = useMemo(() => {
    const secondQualficationItems = Object.keys(SECOND_QUALIFICATIONS)
      .filter((qualificationsKey) =>
        evaluateCondition(SECOND_QUALIFICATIONS[qualificationsKey].whenShouldFieldRender, {
          branch: BRANCH_TYPE.ENERGY,
          firstQualificationEnergy: formik.values.firstQualificationEnergy,
        })
      )
      .map((qualificationsKey) => SECOND_QUALIFICATIONS[qualificationsKey]);
    return secondQualficationItems;
  }, [formik.values.firstQualificationEnergy]);

  useEffect(() => {
    if (!popupOpen) {
      return;
    }
    // used to automatically set the second qualification energy value if there is only one item
    const checkAndSetSingleSecondQualificationEnergy = () => {
      if (secondQualficationEnergyItems.length === 1) {
        formik.setFieldValue(`secondQualificationEnergy`, secondQualficationEnergyItems[0].value);
      }
    };
    checkAndSetSingleSecondQualificationEnergy();
  }, [secondQualficationEnergyItems, popupOpen, formik.values.secondQualificationEnergy]);

  useEffect(() => {
    // Only sync create-popup form state while this popup is open.
    // Opening other popups also changes selectedRow and could trigger a background render loop.
    if (!popupOpen) {
      return;
    }

    if (_.isNull(selectedRow) || !originalExpertData) {
      if (!_.isEqual(formik.values, initialValues)) {
        formik.resetForm({ values: initialValues });
      }
      formik.validateForm(initialValues);
      return;
    }

    const nextValues = { ...originalExpertData };
    if (!_.isEqual(formik.values, nextValues)) {
      formik.setValues(nextValues, true);
    }
  }, [selectedRow, originalExpertData, popupOpen, initialValues]);

  useEffect(() => {
    if (!popupOpen) {
      return;
    }
    if (formik.values.statusEnergy !== EIV_STATUS.INACTIVE) {
      return;
    }
    if (formik.values.firstQualificationEnergy !== '') {
      formik.setFieldValue(`firstQualificationEnergy`, '');
    }
    if (formik.values.secondQualificationEnergy !== '') {
      formik.setFieldValue(`secondQualificationEnergy`, '');
    }
  }, [
    popupOpen,
    formik.values.statusEnergy,
    formik.values.firstQualificationEnergy,
    formik.values.secondQualificationEnergy,
  ]);

  useEffect(() => {
    if (!popupOpen) {
      return;
    }
    if (formik.values.statusGas !== EIV_STATUS.INACTIVE) {
      return;
    }
    if (formik.values.firstQualificationGas !== '') {
      formik.setFieldValue(`firstQualificationGas`, '');
    }
    if (formik.values.secondQualificationGas !== '') {
      formik.setFieldValue(`secondQualificationGas`, '');
    }
  }, [
    popupOpen,
    formik.values.statusGas,
    formik.values.firstQualificationGas,
    formik.values.secondQualificationGas,
  ]);

  const modalContent = () => {
    if (updateSuccess && pendingExpert) {
      return (
        <div className="ne-popup popup-content">
          Es wird ein neuer Link für die Passwort-Vergabe zum Account von{' '}
          <strong>{pendingExpert.completeName}</strong> an die E-Mail-Adresse{' '}
          <strong>{pendingExpert.netzportalEmail}</strong> verschickt.
        </div>
      );
    }

    if (apiError) {
      return (
        <div className="ne-popup popup-content">
          <ErrorBanner>{apiError}</ErrorBanner>
        </div>
      );
    }

    return (
      <div className="ne-popup popup-content">
        <div className="popup-content__popup-field">
          <div className="flex-row">
            <div>Für die Sparte:</div>
            {doesEnergyExist && (
              <LabelCheckbox
                label="Strom"
                id="isExpertEnergyActive"
                name="isExpertEnergyActive"
                error={Boolean(formik.errors?.noBranchSelected)}
                icon={energyIcon}
                disabled={originalExpertData?.isExpertEnergyActive || forceEnergy}
                value={formik.values.isExpertEnergyActive}
                onChange={async (e) => {
                  // setFieldValue is async, wait for value to be set then dispatch change
                  const patch = () => {
                    return formik.setFieldValue(
                      'statusEnergy',
                      e.target.value ? EIV_STATUS.NEW : EIV_STATUS.NOT_SET
                    );
                  };

                  patch().then(() => formik.handleChange(e));
                }}
              />
            )}
            {doesGasExist && (
              <LabelCheckbox
                label="Gas"
                id="isExpertGasActive"
                name="isExpertGasActive"
                error={Boolean(formik.errors?.noBranchSelected)}
                icon={gasIcon}
                disabled={originalExpertData?.isExpertGasActive || forceGas}
                value={formik.values.isExpertGasActive}
                onChange={(e) => {
                  // setFieldValue is async, wait for value to be set then dispatch change
                  const patch = () => {
                    return formik.setFieldValue(
                      'statusGas',
                      e.target.value ? EIV_STATUS.NEW : EIV_STATUS.NOT_SET
                    );
                  };

                  patch().then(() => formik.handleChange(e));
                }}
              />
            )}
          </div>
        </div>
        {!formik.errors.noBranchSelected && (
          <DEBExpertPopupBaseInfoSection
            formik={formik}
            mainFormik={mainFormik}
            selectedRow={selectedRow}
            installerStatusSummary={installerStatusSummary}
          />
        )}
        {!formik.errors.noBranchSelected && areExpertContactsEnabled && (
          <>
            <DEBExpertPopupContactSection formik={formik} />
            {ciamEmailError && <ErrorBanner>{ciamEmailError}</ErrorBanner>}
          </>
        )}
        {!formik.errors.noBranchSelected && (
          <DEBExpertPopupValiditySection
            formik={formik}
            installerStatusSummary={installerStatusSummary}
          />
        )}
        {doesExpertHaveMainBranch(formik.values, installerStatusSummary) && (
          <DEBExpertPopupMainBranchSection
            formik={formik}
            installerStatusSummary={installerStatusSummary}
            firstQualificationGasItems={firstQualificationGasItems}
            firstQualificationEnergyItems={firstQualificationEnergyItems}
          />
        )}
        <FFormDebug values={formik.values} errors={formik.errors} />
      </div>
    );
  };

  const formErrorMessage =
    getExpertContactFormErrorMessage(formik.errors) || getDEExpertPopupErrorMessage(formik);

  const isSubmitting = isSavingContacts || isCheckingCiamEmail;

  const modalButtons = () => {
    if (apiError || updateSuccess) {
      return <ActionButton inverted onClick={onCancel} title="OK" />;
    }

    return (
      <FormActionButtons
        hasErrors={Object.keys(formik.errors).length > 0 || Boolean(ciamEmailError)}
        titleLeft="Abbrechen"
        titleRight="Hinzufügen"
        onClickLeft={onCancel}
        onClickRight={onSubmit}
        loading={isSubmitting}
        errorMessage={formErrorMessage}
      />
    );
  };

  return (
    <CustomModal
      isOpen={popupOpen}
      title="Fachkraft hinzufügen"
      closeCancelEventModal={onCancel}
      ctaButtons={modalButtons()}
      content={modalContent()}
    />
  );
};

DEBCreateExpertPopup.propTypes = {
  mainFormik: PropTypes.object.isRequired,
  installerStatusSummary: PropTypes.object.isRequired,
  gasEntryType: PropTypes.string,
  energyEntryType: PropTypes.string,
  popupOpen: PropTypes.bool,
  setPopupOpen: PropTypes.func,
  selectedRow: PropTypes.number,
  isGuest: PropTypes.bool,
  setSelectedRow: PropTypes.func,
  onExpertContactSaved: PropTypes.func,
};

DEBCreateExpertPopup.defaultProps = {
  popupOpen: false,
  setPopupOpen: () => {},
  selectedRow: null,
  isGuest: false,
  setSelectedRow: () => {},
  gasEntryType: '',
  energyEntryType: '',
  onExpertContactSaved: undefined,
};
